import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {InView} from 'react-intersection-observer';
import SearchTop from './search_top';
import ArticlesStrip from '../magazine/components/ArticlesStrip';
import Benefit from '../shared/benefit/benefit';
import {dropDuplicates, getABParticipationStatus} from '../../app/functions';
import dogIcon from '../../assets/icons/not-found.svg';
import Loader from '../../components/common/loader';
import {AnalyticsParamValues} from '../../constants/analytics';
import {searchBenefits} from '../../redux/actions';
import './results.scss';

const TABS = {
    BENEFITS:'benefits',
    ARTICLES:'articles',
}
const SearchResults = () => {
    const {query} = useParams();
    const dispatch = useDispatch();

    const parameters = useSelector((store) => store.config.Parameters);
    const translations = useSelector((store) => store.config.Translations);

    const [searchResults, setSearchResults] = useState({});
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(TABS.BENEFITS);

    const {abTestingPercentageForSearch} = parameters.searchConfiguration;
    const participationStatus = getABParticipationStatus('searchABTestingIncluded', abTestingPercentageForSearch);

    const areThereAnyResults = (
        Object.keys(searchResults).length > 0 &&
        Object.values(searchResults).some(
            (resultType) => resultType?.metadata?.totalResults > 0
        )
    );
    const areThereMoreResultsForCurrentTab = searchResults?.[activeTab]?.metadata?.totalLeftPages > 0;

    const articlesResults = searchResults?.articles?.records ?? [];
    const benefitsResults = searchResults?.benefits?.records ?? [];
    const activeMeta = searchResults?.[activeTab]?.metadata;
    const showInvertMessage = activeMeta?.usedInvertedQuery && activeMeta?.invertedQuery;
    const getTabs = () => {
        const tabs = []

        if (searchResults?.benefits?.metadata?.totalResults) {
            tabs.push({
                key: TABS.BENEFITS,
                title: `הטבות (${searchResults?.benefits?.metadata?.totalResults})`,
                isDisabled: false,
            })
        }

        if (searchResults?.articles?.metadata?.totalResults) {
            tabs.push({
                key: TABS.ARTICLES,
                title: `כתבות (${searchResults?.articles?.metadata?.totalResults})`,
                isDisabled: false,
            })
        }

        return tabs;
    }

    const handleInitialFetching = () => {
        dispatch(searchBenefits({query, page: 1, size: 20}, handleFetchedResults));
    };

    const handlePageFetching = (inView) => {
        if (!inView) return;

        dispatch(searchBenefits({
            query,
            index: activeTab,
            page: searchResults?.[activeTab]?.metadata?.currentPage + 1,
            size: 20,
        }, handleFetchedResults));
    };

    const handleFetchedResults = (response) => {
        if (!participationStatus) {
            response = formatOldResponseAsNew(response);
        }

        const isNewSearch = (
            Object.keys(searchResults).length === 0 ||
            searchResults?.filters?.query !== response?.filters?.query
        );

        if (isNewSearch) {
            setSearchResults(response);

            if (
                response?.benefits?.metadata?.totalResults === 0 &&
                response?.articles?.metadata?.totalResults > 0
            ) setActiveTab('articles');
            else setActiveTab('benefits');

        } else {
            const newSearchResults = {...searchResults};

            [TABS.BENEFITS, TABS.ARTICLES].forEach((resultType) => {
                if (
                    resultType in response &&
                    resultType in searchResults
                ) {
                    newSearchResults[resultType].records = dropDuplicates([
                        ...newSearchResults[resultType].records,
                        ...response[resultType].records,
                    ]);
                    newSearchResults[resultType].metadata = response[resultType].metadata;
                }
            });

            setSearchResults(newSearchResults);
        }

        setIsInitialLoading(false);
    };

    const formatOldResponseAsNew = (oldResponse) => ({
        benefits: {
            records: oldResponse?.benefits?.records ?? [],
            metadata: {
                totalResults: Number(oldResponse?.benefits?.metaData?.search?.total_results ?? 0),
                totalViewedResults: Number(oldResponse?.benefits?.metaData?.search?.total_viewed_results ?? 0),
                currentPage: Number(oldResponse?.benefits?.metaData?.search?.current_page ?? 0),
                totalLeftPages: Number(oldResponse?.benefits?.metaData?.search?.total_left_pages ?? 0),
                totalPages: 0, // Not exists, irrelevant anyway
            },
        },
        filters: {
            query: oldResponse?.benefits?.metaData?.search?.filters?.query ?? '',
        },
    });

    const formatArticleDataAsExpected = (articleData) => ({
        ...articleData,
        id: articleData.original_id,
        header_name: articleData.title,
        header_sub_name: articleData.sub_title,
        related_category: articleData.related_magazine,
        thumbnail_img_id: articleData.thumbnail_img_path,
    });

    const handelTabSelect = (newValue) => setActiveTab(newValue);

    useEffect(() => {
        setSearchResults({});
        setIsInitialLoading(true);
        handleInitialFetching();
    }, [query]);

    return (
        <main id="free-search-wrapper" className="free-search-wrapper">
            {isInitialLoading && (
                <Loader className="initial-loader"/>
            )}

            {!isInitialLoading && areThereAnyResults && (
                <>
                    <SearchTop
                        query={query}
                        invertedQuery={showInvertMessage ? activeMeta.invertedQuery : null}
                        activeTab={activeTab}
                        tabsList={getTabs()}
                        onTabSelect={handelTabSelect}
                    />

                    <div className="result-wrapper">
                        {activeTab === TABS.BENEFITS && benefitsResults.map((benefit) => (
                            <Benefit
                                key={benefit.id}
                                data={benefit}
                                analyticsSource={AnalyticsParamValues.SEARCH_RESULTS_SOURCE}
                            />
                        ))}

                        {activeTab === TABS.ARTICLES && (
                            <ArticlesStrip
                                articles={articlesResults.map(formatArticleDataAsExpected)}
                                displayTitle={false}
                                analyticsSource={AnalyticsParamValues.SEARCH_RESULTS_SOURCE}
                            />
                        )}

                        {areThereMoreResultsForCurrentTab && (
                            <InView onChange={handlePageFetching}/>
                        )}
                    </div>
                </>
            )}

            {!isInitialLoading && !areThereAnyResults && (
                <div className="no-results">
                    <div className="icon">
                        <img src={dogIcon} alt="Dog Icon"/>
                    </div>

                    <div className="title">
                        {translations['still_no_results']}
                    </div>

                    <div className="text">
                        {translations['no_results_text']}
                    </div>
                </div>
            )}
        </main>
    );
};

export default SearchResults;
